// ============================================================================
// Spain fiscal adapter (AEAT / Verifactu / TicketBAI / Facturae).
// ----------------------------------------------------------------------------
// This is a THIN wrapper over the existing Spanish logic — it does not
// reimplement anything. lib/taxes.js stays the single source of truth for the
// IVA/IRPF/IBAE math; this adapter just exposes it through the country-neutral
// fiscal interface (see ./index.js) so the invoicing pages can call one shape
// regardless of country. Behaviour is byte-for-byte the current Spanish path.
// ============================================================================
import {
  groupLinesByTaxRate,
  sumByRateTotals,
  round2,
  isClientInCatalunya,
} from '../taxes';
import { buildTicketBai, buildTicketBaiXML } from '../ticketbai';
import { buildFacturaeXML } from '../facturae';

// Very light NIF/CIF/NIE shape check (letter/8 digits/letter variants). Kept
// permissive on purpose — the Spanish forms already accept free-form ids.
const NIF_RE = /^[A-Z]?\d{7,8}[A-Z]?$/i;

export const esAdapter = {
  countryCode: 'ES',

  meta: {
    name: 'España',
    currency: 'EUR',
    currencySymbol: '€',
    locale: 'es-ES',
    taxIdLabel: 'NIF / CIF',        // issuer (profiles.cif_nif)
    clientTaxIdLabel: 'NIF / CIF',  // client (clients.tax_id)
    taxLabel: 'IVA',                // primary tax name (editor headers/labels)
  },

  // Tax controls the invoice editor exposes for Spain.
  taxFeatures: {
    recargoEquiv: true,   // recargo de equivalencia
    ibae: true,           // IBAE / IBI (Catalan sugary-drinks tax)
    irpf: true,           // retención IRPF
    suplidos: true,       // suplidos (disbursements)
  },

  // Rate a brand-new blank line starts with (general IVA).
  defaultTaxRate: 21,

  // Per-line tax-rate options for the editor selector (value is a string to
  // match the Select). Mirrors the current hardcoded Spanish IVA options.
  taxRates: [
    { value: '21', label: '21% IVA' },
    { value: '10', label: '10% IVA' },
    { value: '4',  label: '4% IVA' },
    { value: '0',  label: '0% IVA' },
  ],

  // Normalized totals for the UI + printed template. Delegates the per-rate
  // math to lib/taxes.js. `opts` carries the Spanish document flags:
  //   { recargoEquiv, applyIbi, irpfRate, suplidosTotal }
  computeTotals(lines, opts = {}) {
    const {
      recargoEquiv = false,
      applyIbi = true,
      irpfRate = 0,
      suplidosTotal = 0,
    } = opts;
    const byRate = groupLinesByTaxRate(lines, { recargoEquiv, applyIbi });
    const { subtotal, ivaTotal, recargoTotal, ibiTotal } = sumByRateTotals(byRate);
    const irpfTotal = round2(subtotal * (Number(irpfRate) || 0) / 100);
    const suplidos = round2(Number(suplidosTotal) || 0);
    const grandTotal = round2(subtotal + ibiTotal + ivaTotal + recargoTotal + suplidos - irpfTotal);
    return {
      byRate,
      subtotal,
      taxTotal: ivaTotal,
      taxLabel: 'IVA',
      extraTotals: [
        { key: 'recargo', label: 'Recargo de equivalencia', amount: recargoTotal },
        { key: 'ibae', label: 'IBAE', amount: ibiTotal },
        { key: 'suplidos', label: 'Suplidos', amount: suplidos },
      ].filter((x) => x.amount),
      deductions: [
        { key: 'irpf', label: `Retención IRPF (${irpfRate}%)`, amount: irpfTotal },
      ].filter((x) => x.amount),
      grandTotal,
    };
  },

  requiredClientFields() {
    return ['tax_id'];
  },

  validateTaxId(id) {
    return NIF_RE.test(String(id || '').trim());
  },

  // Auto-apply a client's Spanish tax regime when selected in the editor
  // (mirrors InvoiceCreate.jsx:431-432 — surfaced here so Phase 1 can call it
  // through the adapter instead of importing isClientInCatalunya directly).
  clientTaxDefaults(client) {
    return {
      recargoEquiv: !!client?.recargo_equiv,
      applyIbi: isClientInCatalunya(client) || !!client?.apply_ibi,
    };
  },

  einvoice: {
    enabled: true,
    featureKey: null,           // Spain e-invoice is not behind a feature flag
    label: 'Verifactu / TicketBAI',
    // Spain generates the artifact client-side (no network submit today). This
    // keeps InvoiceView's existing behaviour: build the id/QR + XML locally.
    async submit({ invoice, lines, profile, client }) {
      const province = profile?.face_dir3?.province || 'gipuzkoa';
      const tbai = buildTicketBai({ invoice, profile, province });
      const xml = buildTicketBaiXML({ invoice, lines, profile, client, tbai });
      const facturae = buildFacturaeXML({ invoice, lines, profile, client });
      return {
        fiscalNumber: tbai.tbaiId,
        qr: tbai.qrUrl,
        status: 'generated',
        raw: { ticketBaiXml: xml, facturaeXml: facturae },
      };
    },
  },
};
